import { Fragment } from "react";

/**
 * The proposal document — a print-ready, four-page layout.
 *
 * Nothing here is authored in the frontend. Prices come from
 * /proposals/calculate, and every other word (company details, offerings,
 * includes, trust stats, terms, footer) comes from /proposal-template, so the
 * team can change the document without a deploy.
 */

const inr = (n) =>
  n === null || n === undefined ? "—" : `₹${Math.round(Number(n)).toLocaleString("en-IN")}`;
const num = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN"));

const fmtDate = (d) =>
  d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

/** Rupees in words — printed under the quotation total, as invoices do. */
const words = (value) => {
  let n = Math.round(Number(value) || 0);
  if (n === 0) return "Zero";
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (x) => (x < 20 ? a[x] : b[Math.floor(x / 10)] + (x % 10 ? ` ${a[x % 10]}` : ""));
  const three = (x) =>
    (Math.floor(x / 100) ? `${a[Math.floor(x / 100)]} Hundred${x % 100 ? " " : ""}` : "") +
    (x % 100 ? two(x % 100) : "");
  let s = "";
  const cr = Math.floor(n / 1e7); n %= 1e7;
  const la = Math.floor(n / 1e5); n %= 1e5;
  const th = Math.floor(n / 1e3); n %= 1e3;
  if (cr) s += `${three(cr)} Crore `;
  if (la) s += `${three(la)} Lakh `;
  if (th) s += `${three(th)} Thousand `;
  if (n) s += three(n);
  return s.trim();
};

/* Template copy arrives as HTML strings (it carries <b>/<i> emphasis), so it is
   rendered as markup. It comes from our own backend, not from user input, which
   is what makes that safe. */
const Html = ({ as: Tag = "span", html, ...rest }) => (
  <Tag {...rest} dangerouslySetInnerHTML={{ __html: html || "" }} />
);

/**
 * The order the sections shipped in. Used only when the server sends no
 * section_order, so an older config still renders the whole document.
 */
export const DEFAULT_SECTION_ORDER = [
  "executive_summary", "why_entermark", "offerings",
  "common_includes", "how_we_start", "founder", "terms",
];

/** Initials for the founder avatar when no photo is configured. */
const initials = (name = "") =>
  name.trim().split(/\s+/).map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";

const Signature = ({ sealId }) => (
  <>
    <svg className="sig-draw" viewBox="0 0 300 108" fill="none" stroke="#12245C" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14,76 C9,40 33,16 42,40 C50,60 22,58 31,61 C46,61 40,77 56,71 C68,67 60,42 74,53 C87,63 79,27 88,29 C94,53 92,74 106,67 C118,61 111,43 122,55 C131,65 142,67 152,58" />
      <path d="M150,60 C150,32 167,25 179,44 C190,62 159,74 150,65 C172,67 188,57 198,64 C208,71 203,50 212,58 C220,66 210,76 222,71 C236,67 250,60 270,68" />
      <path d="M8,92 C74,80 154,100 226,84 C254,78 274,87 294,81" strokeWidth="2.2" opacity=".85" />
    </svg>
    <svg className="seal-svg" viewBox="0 0 100 100">
      <defs>
        <path id={`${sealId}a`} d="M12,50 a38,38 0 1,1 76,0" />
        <path id={`${sealId}b`} d="M17,50 a33,33 0 1,0 66,0" />
      </defs>
      <g fill="none" stroke="#12245C" strokeOpacity=".78">
        <circle cx="50" cy="50" r="47" strokeWidth="1.6" />
        <circle cx="50" cy="50" r="40.5" strokeWidth="1" />
      </g>
      <g fill="#12245C" fillOpacity=".85" fontFamily="Arial,Helvetica,sans-serif">
        <text fontSize="6.7" fontWeight="700" letterSpacing="0.5" textAnchor="middle">
          <textPath href={`#${sealId}a`} startOffset="50%">ENTERFIRST PRIVATE LIMITED</textPath>
        </text>
        <text fontSize="5.6" fontWeight="700" letterSpacing="1" textAnchor="middle">
          <textPath href={`#${sealId}b`} startOffset="50%">NEW DELHI · INDIA</textPath>
        </text>
        <text x="50" y="43" fontSize="6.4" fontWeight="800" textAnchor="middle">AUTHORISED</text>
        <text x="50" y="51" fontSize="6.4" fontWeight="800" textAnchor="middle">SIGNATORY</text>
        <text x="50" y="64" fontSize="12" textAnchor="middle">★</text>
      </g>
    </svg>
  </>
);

const ProposalDocument = ({ form, quote, template, quoteNo, segmentLabel }) => {
  const company = template?.company || {};
  const client = form.client_name?.trim() || "Your Company";
  /* The document is client-facing: it names, addresses and is billed to a real
     person, so it stays hidden until all four of those are known. Listing what
     is still missing beats a blank stage. */
  const missing = [];
  if (!form.client_name?.trim()) missing.push("client / company name");
  if (!form.contact_name?.trim()) missing.push("contact name");
  if (!form.email?.trim()) missing.push("contact email");
  if (!form.phone?.trim()) missing.push("contact phone");

  if (missing.length) {
    return (
      <div className="empty">
        <b>Add the prospect’s details to generate a personalised proposal.</b>
        <br /><br />
        Required: {missing.join(", ")}.
      </div>
    );
  }
  if (!quote) {
    return <div className="empty">Fill the pricing inputs to build the proposal.</div>;
  }

  const seg = quote.segment || form.segment;
  /* A custom segment has no shipped audience line — it is named after itself,
     which is what the admin who created it chose to call it. */
  const audience =
    seg === "corporate" ? "Corporates & Brand Teams"
    : seg === "attorney" ? "Attorneys & Law Firms"
    : segmentLabel || seg;
  const today = new Date();
  const validity = template?.validity_days ?? 30;
  const validUntil = new Date(today.getTime() + validity * 864e5);

  /* Past the enterprise threshold the API returns no pricing at all — the whole
     document degrades to a "talk to sales" quotation rather than showing zeros. */
  const isCustom = quote.custom === true;
  const p = quote.pricing || {};
  const li = quote.line_items || {};
  const d = quote.discounts || {};
  const months = p.months || (form.frequency === "yearly" ? 12 : 3);
  const termLabel = months === 12 ? "Annual" : "Quarterly";
  const termWord = months === 12 ? "year" : "quarter";
  const firstLabel = months === 12 ? "Year 1" : "Q1";
  const periodDays = months === 12 ? 365 : 90;
  const grace = months === 12 ? 14 : 7;

  const start = form.start_date ? new Date(`${form.start_date}T00:00:00`) : today;
  const end = new Date(start); end.setFullYear(end.getFullYear() + 1); end.setDate(end.getDate() - 1);
  const nextDue = new Date(start); nextDue.setDate(nextDue.getDate() + periodDays);

  const features = quote.plan_details?.features || [];
  /* The API resolves the three segment-keyed blocks when it is asked for a
     segment. The raw lookups below are the fallback for an older response, and
     `default` is what a custom segment with no copy of its own inherits. */
  const resolved = template?.resolved || {};
  const offerings =
    resolved.offerings || template?.offerings?.[seg] || template?.offerings?.default || [];
  const includes = template?.common_includes || [];
  /* why_entermark carries the heading, the tiles and the cards together;
     trust_stats is the older shape and is only used when the tiles are empty. */
  const why = template?.why_entermark;
  const stats = why?.stats?.length ? why.stats : template?.trust_stats || [];
  const whyCards = why?.cards || [];
  const proposalTerms = template?.terms?.proposal || [];
  const quotationTerms = template?.terms?.quotation || [];

  /* Template copy carries tokens the backend cannot resolve — it does not know
     the prospect. Every string passes through here before it is rendered,
     including the HTML ones. */
  const fill = (text) =>
    typeof text === "string"
      ? text
          .replace(/\{\{client_name\}\}/g, client)
          .replace(/\{\{contact_name\}\}/g, form.contact_name?.trim() || "")
          .replace(/\{\{marks\}\}/g, num(quote.marks))
          .replace(/\{\{brand_name\}\}/g, company.brand_name || "")
          .replace(/\{\{plan\}\}/g, quote.plan || "")
      : "";

  /* Every block is optional so the backend can ship them one at a time — a
     missing field must leave no empty heading behind. */
  /* The admin owns the order of the narrative sections. A key missing from the
     list is a hidden section, not a bug — and an empty or absent list falls back
     to the shipped order so an older config still renders everything. */
  const order = template?.section_order?.length ? template.section_order : DEFAULT_SECTION_ORDER;
  const customSections = template?.custom_sections || [];

  const coverSubtitle =
    resolved.cover_subtitle || template?.cover_subtitle?.[seg] || template?.cover_subtitle?.default;
  const summary = template?.executive_summary;
  const summaryBody =
    resolved.executive_summary_body || summary?.body?.[seg] || summary?.body?.default;
  const howWeStart = template?.how_we_start;
  const founder = template?.founder;

  /**
   * One renderer per orderable section. Returns null when the section has no
   * content, so a reordered list never prints an empty heading.
   */
  const renderSection = (key) => {
    if (String(key).startsWith("custom:")) {
      const id = String(key).slice("custom:".length);
      const custom = customSections.find((c) => String(c.id) === id);
      // A disabled or deleted custom section simply drops out of the document.
      if (!custom || custom.enabled === false || (!custom.title && !custom.body)) return null;
      return (
        <div className="sec">
          {custom.title && <h3>{fill(custom.title)}</h3>}
          {custom.subheading && <Html as="p" className="sh" html={fill(custom.subheading)} />}
          {custom.body && (
            <Html as="div" style={{ fontSize: 12.5, color: "#2A3547", lineHeight: 1.65 }} html={fill(custom.body)} />
          )}
        </div>
      );
    }

    switch (key) {
      case "executive_summary":
        if (!summaryBody) return null;
        return (
          <div className="sec">
            <h3>{summary.heading || "Executive summary"}</h3>
            {summary.subheading && <Html as="p" className="sh" html={fill(summary.subheading)} />}
            <Html as="p" style={{ fontSize: 12.5, color: "#2A3547", lineHeight: 1.65, margin: 0 }} html={fill(summaryBody)} />
          </div>
        );

      case "offerings":
        if (!offerings.length) return null;
        return (
          <div className="topoffers">
            <div className="oh">
              <span className="tag">⚡ Top offerings for {seg === "corporate" ? "brand teams" : seg === "attorney" ? "law firms" : (segmentLabel || seg)}</span>
            </div>
            <div className="offers">
              {offerings.map((o, i) => (
                <div className="offer" key={i}>
                  <div className="ic">{o.icon}</div>
                  <div><h4>{o.title}</h4><Html as="p" html={fill(o.description)} /></div>
                </div>
              ))}
            </div>
          </div>
        );

      case "why_entermark":
        if (!stats.length && !whyCards.length) return null;
        return (
          <div className="sec">
            <h3>{why?.heading || `Why ${company.brand_name}`}</h3>
            {why?.subheading && <Html as="p" className="sh" html={fill(why.subheading)} />}
            {stats.length > 0 && (
              <div className="tiles">
                {stats.map((s, i) => (
                  <div className="tile" key={i}><b>{s.value}</b><span>{s.label}</span></div>
                ))}
              </div>
            )}
            {whyCards.length > 0 && (
              <div className="grid2c" style={{ marginTop: 12 }}>
                {whyCards.map((c, i) => (
                  <div className="card" key={i}>
                    <h4>{c.title}</h4>
                    <Html as="p" html={fill(c.description)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "common_includes":
        if (!includes.length) return null;
        return (
          <div className="sec">
            <h3>Every plan includes</h3>
            <ul className="feats">{includes.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </div>
        );

      case "how_we_start":
        if (!howWeStart?.steps?.length) return null;
        return (
          <div className="sec">
            <h3>{howWeStart.heading || "How we start"}</h3>
            {/* Ordered on purpose — these are sequential steps, not a menu. */}
            <ol style={{ fontSize: 12.5, color: "#2A3547", lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
              {howWeStart.steps.map((s, i) => <Html as="li" key={i} html={fill(s)} />)}
            </ol>
          </div>
        );

      case "founder":
        if (!founder?.name) return null;
        return (
          <div className="sec">
            <h3>{founder.heading || "Meet the founder"}</h3>
            {founder.subheading && <Html as="p" className="sh" html={fill(founder.subheading)} />}
            <div className="leader">
              {/* An absolute URL when configured; initials otherwise, so the card
                  never renders a broken image. */}
              {founder.photo_url
                ? <img src={founder.photo_url} alt={founder.name} />
                : <div className="mono-av">{initials(founder.name)}</div>}
              <div>
                <div className="lname">{founder.name}</div>
                {founder.title && <div className="ltitle">{founder.title}</div>}
                {founder.bio && <Html as="p" html={fill(founder.bio)} />}
                {founder.quote && <p className="lcomment">“{founder.quote}”</p>}
              </div>
            </div>
          </div>
        );

      case "terms":
        if (!proposalTerms.length) return null;
        return (
          <div className="sec">
            <h3>Terms &amp; Conditions</h3>
            <ol className="terms">{proposalTerms.map((t, i) => <Html as="li" key={i} html={fill(t)} />)}</ol>
          </div>
        );

      default:
        // An unknown key from a newer backend is ignored rather than crashing.
        return null;
    }
  };

  const totalDiscPct = (d.auto_percent || 0) + (d.sales_percent || 0);
  const showRateRow = seg === "attorney" || li.is_custom_rate;

  const CompanyContact = () => (
    <div className="qp-lines">
      {company.city} · CIN {company.cin}<br />
      GSTIN {company.gstin} · SAC {company.sac}<br />
      {company.email} · {company.whatsapp} · {company.website}
    </div>
  );

  const QuotationHead = () => (
    <>
      <div className="qp-head">
        <div className="qp-co">
          <div className="qp-logo">{(company.brand_name || "E").charAt(0)}</div>
          <div>
            <h2>{company.legal_name}</h2>
            <div className="qp-sub">{company.brand_name} — AI Trademark Suite</div>
            <CompanyContact />
          </div>
        </div>
        <div className="qp-meta">
          <div className="qp-title">QUOTATION</div>
          <table><tbody>
            <tr><td>Quotation No</td><td><b>{quoteNo}</b></td></tr>
            <tr><td>Date</td><td><b>{fmtDate(today)}</b></td></tr>
            <tr><td>Valid until</td><td><b>{fmtDate(validUntil)}</b></td></tr>
          </tbody></table>
        </div>
      </div>
      <div className="qp-parties">
        <div className="qp-party">
          <div className="qp-h">Bill to</div>
          <div className="qp-nm">{client}</div>
          <div className="qp-dt">
            {form.contact_name && <>Attn: <b>{form.contact_name}</b><br /></>}
            {form.email}{form.email && form.phone ? " · " : ""}{form.phone}
            {form.address && <><br />{form.address}</>}
            {form.gstin && <><br />GSTIN: {form.gstin}</>}
          </div>
        </div>
        <div className="qp-party">
          <div className="qp-h">Engagement</div>
          <div className="qp-dt">
            {audience} · <b>{num(quote.marks)}</b> trademarks · {form.users || 1} user(s)<br />
            Plan: <b>{quote.plan}</b> · billed {months === 12 ? "annually" : "quarterly"} · 12-month term
          </div>
        </div>
      </div>
    </>
  );

  const QuotationTerms = () => (
    <>
      <div className="qp-terms">
        <h3>Terms &amp; Conditions</h3>
        <ol>{quotationTerms.map((t, i) => <Html as="li" key={i} html={fill(t)} />)}</ol>
      </div>
      <div className="qp-sign">
        <div className="qp-sigL"><div className="qp-sigbox" />Client acceptance — signature, name &amp; date</div>
        <div className="qp-sigL">
          <div className="qp-sigbox"><Signature sealId="qseal" /></div>
          For <b>{company.legal_name}</b><br />
          <b>{company.signatory_name}</b> · {company.signatory_role}
        </div>
      </div>
    </>
  );

  return (
    <div className={`theme-${seg}`}>

      {/* ── PAGE 1 ── */}
      <div className="page">
        <span className="pagetag">1 / 4</span>
        <div className="cover">
          <span className="mono">™</span>
          <div className="covtop">
            <div style={{ display: "flex", alignItems: "center", gap: 9, fontWeight: 800, fontSize: 16 }}>
              <span style={{ width: 26, height: 26, borderRadius: 8, background: "var(--grad)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {(company.brand_name || "E").charAt(0)}
              </span>
              {company.brand_name}
            </div>
            <div className="rate"><span className="stars">★★★★★</span> 4.9/5 · 1,000+ IP pros</div>
          </div>
          <div className="lux-badge">◆ World's First AI-Powered Trademark Suite</div>
          <div className="kick" style={{ marginTop: 14 }}>
            Proposal · Prepared exclusively for {form.contact_name || client} · Confidential
          </div>
          <h1>
            {seg === "attorney" ? "AI trademark practice platform for" : "AI trademark protection for"}
            <br /><span className="hl">{client}</span>
          </h1>
          {coverSubtitle && (
            <Html as="div" style={{ color: "#C9CCEB", fontSize: 13, maxWidth: 540 }} html={fill(coverSubtitle)} />
          )}
          <div className="covchips">
            <span className="covchip"><span className="cl">WEB</span> <b>{company.website}</b></span>
            <span className="covchip"><span className="cl">EMAIL</span> <b>{company.email}</b></span>
            <span className="covchip"><span className="cl">WHATSAPP</span> <b>{company.whatsapp}</b></span>
          </div>
          <div className="meta">
            Prepared for <b>{form.contact_name || "—"}</b> · {client}
            {form.email && <> · <b>{form.email}</b></>}
            {form.phone && <> · <b>{form.phone}</b></>}
            <br />{audience} &nbsp;·&nbsp; {fmtDate(today)} &nbsp;·&nbsp;
            <b>{num(quote.marks)} trademarks</b> &nbsp;·&nbsp; <b>{form.users || 1} users</b>
          </div>
        </div>


        <div className="sec">
          <h3>Your plan &amp; investment</h3>
          <p className="sh">
            Per month, billed {termLabel.toLowerCase()} · 12-month lock-in · GST {p.gst_percent ?? 18}% extra ·
            government registry fees at cost.
          </p>
          <table className="pt"><tbody>
            {isCustom ? (
              <>
                <tr><td>Package</td><td>Enterprise — custom</td></tr>
                <tr className="big"><td>Pricing</td><td>Custom quote (talk to sales)</td></tr>
              </>
            ) : (
              <>
                <tr><td>Package</td><td>{quote.plan}</td></tr>
                {showRateRow && (
                  <tr>
                    <td>Per-trademark rate</td>
                    <td>
                      ₹{li.per_mark_rate}/mark/yr{" "}
                      {li.is_custom_rate
                        ? <span style={{ color: "var(--ind2)", fontWeight: 700 }}>(custom)</span>
                        : <span style={{ color: "var(--mut2)" }}>(recommended)</span>}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>Payment</td>
                  <td>
                    {termLabel} · {d.auto_percent || 0}% prepay discount
                    {d.sales_percent > 0 ? ` + ${d.sales_percent}% additional` : ""}
                  </td>
                </tr>
                <tr><td>Subscription term</td><td>{fmtDate(start)} – {fmtDate(end)}</td></tr>
                <tr><td>Billing cycle</td><td>Every {periodDays} days · {grace}-day grace period</td></tr>
                <tr><td>List price / month</td><td>{inr(li.base_monthly)}</td></tr>
                {li.addons_monthly > 0 && <tr><td>Add-ons / month</td><td>{inr(li.addons_monthly)}</td></tr>}
                {p.discount_amt > 0 && (
                  <tr><td>Discount ({totalDiscPct}%)</td><td style={{ color: "var(--emr)" }}>− {inr(p.discount_amt)}</td></tr>
                )}
                <tr className="big"><td>Net price / month</td><td>{inr(p.monthly_net)}</td></tr>
                {p.onboarding > 0 && (
                  <tr>
                    <td>
                      One-time onboarding
                      {d.onboarding_percent > 0 && (
                        <span style={{ color: "var(--emr)", fontWeight: 700 }}> ({d.onboarding_percent}% off)</span>
                      )}
                    </td>
                    <td>
                      {d.onboarding_percent > 0
                        ? <><s style={{ color: "var(--mut2)" }}>{inr(p.onboarding)}</s> &nbsp;{inr(p.onboarding_net)}</>
                        : inr(p.onboarding)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>{termLabel} invoice (incl. {p.gst_percent ?? 18}% GST)</td>
                  <td>{inr(p.term_invoice)}</td>
                </tr>
              </>
            )}
          </tbody></table>
          {form.notes && (
            <p style={{ fontSize: 12, color: "var(--mut)", marginTop: 12 }}><b>Note:</b> {form.notes}</p>
          )}
        </div>
      </div>

      {/* ── PAGE 2 ── */}
      <div className="page">
        <span className="pagetag">2 / 4</span>
        <div className="sec">
          <h3>What {client} gets <span className="pill">{quote.plan_details?.name || quote.plan}</span></h3>
          {quote.plan_details?.tagline && <p className="sh">{quote.plan_details.tagline}</p>}
          {features.length > 0 ? (
            <ul className="feats">
              {features.map((f, i) => (
                <li key={i} style={f.highlight ? { fontWeight: 700, color: "var(--ink)" } : undefined}>
                  {f.name}
                </li>
              ))}
            </ul>
          ) : (
            /* plan_id is unmapped on the rate card — say so instead of printing
               an empty section into a client-facing document. */
            <p style={{ fontSize: 12.5, color: "var(--amb)" }}>
              No features are mapped to this plan yet — set its plan on the Rate Card.
            </p>
          )}
        </div>
        {/* Everything below is ordered by section_order, so the admin controls
            the narrative. The fixed anchors — cover, pricing, features, the cost
            breakdown, signatures and the quotation — stay where they are. */}
        {order.map((key) => (
          <Fragment key={key}>{renderSection(key)}</Fragment>
        ))}
      </div>

      {/* ── PAGE 3 ── */}
      <div className="page">
        <span className="pagetag">3 / 4</span>
        {!isCustom && (
          <div className="sec">
            <h3>Detailed cost breakdown</h3>
            <p className="sh">
              Figures pre-tax unless stated; GST {p.gst_percent ?? 18}% added on invoice; government fees billed at cost.
            </p>
            <table className="pt"><tbody>
              <tr><td>Package</td><td>{quote.plan}</td></tr>
              <tr><td>List price / month</td><td>{inr(li.base_monthly)}</td></tr>
              {li.addons_monthly > 0 && (
                <tr>
                  <td>
                    Add-ons / month ({li.extra_users || 0} extra users
                    {li.extra_clients ? `, ${li.extra_clients} extra clients` : ""})
                  </td>
                  <td>{inr(li.addons_monthly)}</td>
                </tr>
              )}
              <tr><td>Subtotal / month</td><td>{inr(p.monthly_sub)}</td></tr>
              {p.auto_discount_amt > 0 && (
                <tr><td>Prepay discount ({d.auto_percent}%)</td><td style={{ color: "var(--emr)" }}>− {inr(p.auto_discount_amt)}</td></tr>
              )}
              {p.sales_discount_amt > 0 && (
                <tr><td>Sales discount ({d.sales_percent}%)</td><td style={{ color: "var(--emr)" }}>− {inr(p.sales_discount_amt)}</td></tr>
              )}
              <tr className="big"><td>Net price / month</td><td>{inr(p.monthly_net)}</td></tr>
              <tr>
                <td>{months === 12 ? "Per year (× 12 months)" : "Per quarter (× 3 months)"}</td>
                <td>{inr(p.per_term)}</td>
              </tr>
              <tr><td>GST ({p.gst_percent ?? 18}%)</td><td>{inr(p.gst)}</td></tr>
              <tr className="big"><td>{termLabel} invoice (incl. GST)</td><td>{inr(p.term_invoice)}</td></tr>
              {p.onboarding > 0 && (
                <>
                  <tr><td>One-time onboarding (list)</td><td>{inr(p.onboarding)}</td></tr>
                  {p.onboarding_discount_amt > 0 && (
                    <tr><td>Onboarding discount ({d.onboarding_percent}%)</td><td style={{ color: "var(--emr)" }}>− {inr(p.onboarding_discount_amt)}</td></tr>
                  )}
                  <tr><td>First invoice ({firstLabel} + onboarding)</td><td>{inr(p.first_invoice)}</td></tr>
                </>
              )}
              <tr><td>Estimated annual value</td><td>{inr(p.annual_value)}</td></tr>
            </tbody></table>
          </div>
        )}


        <div className="sec">
          <h3>Acceptance &amp; Signatures</h3>
          <p className="sh">Sign both blocks to proceed — or simply reply to this proposal to activate your free trial.</p>
          <div className="accept">
            <div className="sigcard">
              <div className="who">Accepted for the Client</div>
              <div className="org">{client}</div>
              <div className="cin">Confirms acceptance of the pricing, scope &amp; terms set out in this proposal.</div>
              <div className="sigbox"><span>Signature &amp; company seal</span></div>
              <div className="sigrow">
                <div className="fld"><label>Name</label></div>
                <div className="fld"><label>Designation</label></div>
                <div className="fld wide"><label>Date</label></div>
              </div>
            </div>
            <div className="sigcard alt">
              <div className="who">For and on behalf of {company.brand_name}</div>
              <div className="org">{company.legal_name}</div>
              <div className="cin">CIN {company.cin} · {company.city}</div>
              <div className="sigbox"><Signature sealId="pseal" /><span>Signature &amp; company seal</span></div>
              <div className="sigrow">
                <div className="fld"><label>Name</label>&nbsp;<b>{company.signatory_name}</b></div>
                <div className="fld"><label>Designation</label>&nbsp;<b>{company.signatory_role}</b></div>
                <div className="fld wide"><label>Date</label>&nbsp;<b>{fmtDate(today)}</b></div>
              </div>
            </div>
          </div>
        </div>

        <div className="contactband">
          <span className="mono">{(company.brand_name || "E").charAt(0)}</span>
          <h3>Let's protect {client}'s brand</h3>
          <p className="cbsub">Reply to this proposal to activate your 14-day free trial, or reach us on any channel below.</p>
          <div className="cbgrid">
            <div className="cbitem"><div className="l">Website</div><div className="v">{company.website}</div></div>
            <div className="cbitem"><div className="l">Email</div><div className="v">{company.email}</div></div>
            <div className="cbitem"><div className="l">WhatsApp / Call</div><div className="v">{company.whatsapp}</div></div>
            <div className="cbitem"><div className="l">Registered office</div><div className="v">{company.office_short}</div></div>
          </div>
          <a className="cbcta" href={`https://${company.website}`} target="_blank" rel="noreferrer">
            Book a live demo &nbsp;→&nbsp; {company.website}
          </a>
        </div>

        <div className="foot">
          <b>{company.brand_name}</b> — {company.legal_name} · CIN {company.cin}
          {company.gstin && <> · GSTIN {company.gstin}</>} · {company.email} ·
          WhatsApp {company.whatsapp} · {company.website}
          <br />
          <span style={{ color: "#6E76A4" }}>
            {template?.footer_note && <>{fill(template.footer_note)} </>}
            {/* The reader needs to know how long these numbers hold. */}
            {validity ? `Valid for ${validity} days from ${fmtDate(today)}.` : ""}
          </span>
        </div>
      </div>

      {/* ── PAGE 4 · FORMAL QUOTATION ── */}
      <div className="page">
        <span className="pagetag">4 / 4</span>
        <QuotationHead />
        {isCustom ? (
          <>
            <div className="qp-sec">
              <table className="qp-items"><tbody>
                <tr><th>#</th><th>Description</th><th className="r">Amount</th></tr>
                <tr>
                  <td>1</td>
                  <td>
                    <b>{company.brand_name} Enterprise — custom engagement</b><br />
                    <span className="qp-mut">{audience} · {num(quote.marks)} trademarks</span>
                  </td>
                  <td className="r">On request</td>
                </tr>
              </tbody></table>
            </div>
            <div className="qp-note">
              {quote.message || "Quoted as a custom Enterprise engagement; our team will share tailored commercials."}
            </div>
          </>
        ) : (
          <>
            <div className="qp-sec">
              <table className="qp-items"><tbody>
                <tr>
                  <th>#</th><th>Description</th><th className="r">Qty (mo)</th>
                  <th className="r">Rate</th><th className="r">Amount</th>
                </tr>
                <tr>
                  <td>1</td>
                  <td>
                    <b>{company.brand_name} {quote.plan} subscription</b><br />
                    <span className="qp-mut">
                      {audience} · {num(quote.marks)} TMs · {form.users || 1} user(s)
                      {showRateRow && ` · ₹${li.per_mark_rate}/mark/yr${li.is_custom_rate ? " (custom)" : ""}`}
                      {" "}· SAC {company.sac} · {termLabel} billing
                    </span>
                  </td>
                  <td className="r">{months}</td>
                  <td className="r">{inr(li.base_monthly)}</td>
                  <td className="r">{inr((li.base_monthly || 0) * months)}</td>
                </tr>
                {li.addons_monthly > 0 && (
                  <tr>
                    <td>2</td>
                    <td>
                      <b>Add-ons</b><br />
                      <span className="qp-mut">
                        {li.extra_users ? `${li.extra_users} extra ${li.free_clients !== undefined && li.free_clients !== null ? "seat(s)" : "user(s)"}` : ""}
                        {li.extra_clients ? ` · ${li.extra_clients} extra client(s)` : ""}
                      </span>
                    </td>
                    <td className="r">{months}</td>
                    <td className="r">{inr(li.addons_monthly)}</td>
                    <td className="r">{inr(li.addons_monthly * months)}</td>
                  </tr>
                )}
                {p.onboarding > 0 && (
                  <tr>
                    <td>{li.addons_monthly > 0 ? 3 : 2}</td>
                    <td>
                      <b>Onboarding &amp; setup</b> (one-time)
                      {d.onboarding_percent > 0 && <span className="qp-grn"> — {d.onboarding_percent}% off</span>}
                      <br /><span className="qp-mut">Portfolio import, configuration &amp; training</span>
                    </td>
                    <td className="r">1</td>
                    <td className="r">{inr(p.onboarding)}</td>
                    <td className="r">{inr(p.onboarding_net)}</td>
                  </tr>
                )}
              </tbody></table>
            </div>

            <div className="qp-totals">
              <div className="qp-pay">
                <div className="qp-h">Bank &amp; payment details</div>
                <div className="bankcard">
                  <div className="bank-top">
                    <span className="bank-ic">🏦</span>
                    <div>
                      <div className="bank-nm">{company.bank?.name}</div>
                      <div className="bank-sub">A/C in favour of {company.bank?.account_name || company.legal_name}</div>
                    </div>
                  </div>
                  <div className="bank-grid">
                    <div className="bcell"><span className="bl">Account No.</span><span className="bv">{company.bank?.account_no}</span></div>
                    <div className="bcell"><span className="bl">IFSC (RTGS/NEFT)</span><span className="bv">{company.bank?.ifsc}</span></div>
                    <div className="bcell"><span className="bl">Account name</span><span className="bv">{company.bank?.account_name || company.legal_name}</span></div>
                    <div className="bcell"><span className="bl">UPI</span><span className="bv">{company.bank?.upi || "—"}</span></div>
                  </div>
                </div>
                <div className="qp-terminfo">
                  Term <b>{fmtDate(start)} – {fmtDate(end)}</b> · billing every {periodDays} days ·
                  {" "}{grace}-day grace · next due <b>{fmtDate(nextDue)}</b>.<br />
                  Recurring <b>{inr(p.term_invoice)}</b>/{termWord} (incl. GST) · est. annual value{" "}
                  <b>{inr(p.annual_value)}</b> + GST.
                </div>
              </div>

              <table className="qp-sum"><tbody>
                <tr>
                  <td>Subtotal ({firstLabel} + onboarding)</td>
                  <td className="r">
                    {inr((p.monthly_sub || 0) * months + (p.onboarding || 0))}
                  </td>
                </tr>
                {p.discount_amt > 0 && (
                  <tr className="grn">
                    <td>Discount ({totalDiscPct}%)</td>
                    <td className="r">− {inr((p.discount_amt || 0) * months)}</td>
                  </tr>
                )}
                {p.onboarding_discount_amt > 0 && (
                  <tr className="grn">
                    <td>Onboarding discount ({d.onboarding_percent}%)</td>
                    <td className="r">− {inr(p.onboarding_discount_amt)}</td>
                  </tr>
                )}
                <tr><td>Taxable value</td><td className="r">{inr((p.per_term || 0) + (p.onboarding_net || 0))}</td></tr>
                <tr><td>IGST @ {p.gst_percent ?? 18}%</td><td className="r">{inr(((p.per_term || 0) + (p.onboarding_net || 0)) * ((p.gst_percent ?? 18) / 100))}</td></tr>
                <tr className="grand">
                  <td>Grand Total ({firstLabel} payable)</td>
                  <td className="r">{inr(p.first_invoice || p.term_invoice)}</td>
                </tr>
              </tbody></table>
            </div>

            <div className="qp-words">
              <b>Amount in words:</b> Rupees {words(p.first_invoice || p.term_invoice)} Only.
            </div>
            <div className="qp-note">
              IGST @ {p.gst_percent ?? 18}% shown for inter-state supply; for a Delhi client the same rate is
              CGST + SGST. Government / registry fees additional at actuals. This is a quotation, not a tax invoice.
            </div>
          </>
        )}
        <QuotationTerms />
      </div>
    </div>
  );
};

export default ProposalDocument;
